import { useState } from "react";
import { Link } from "react-router-dom";
import { useHelp, type Help } from "../../api/admin";
import { useMe } from "../../auth/session";
import { JOB_FLOW, QUESTIONS, matchesQuestion } from "./helpContent";
import "../../styles/help.css";

/** Help, for anyone signed in: how a job moves through the app, the
 * questions people actually ask, and the handbook and walkthrough. */
export default function HelpPage() {
  const me = useMe();
  const help = useHelp();
  const reachable = new Set((me.data?.screens ?? []).map((screen) => screen.href));
  return <HelpView reachable={reachable} help={help.data} helpError={help.error?.message ?? null} />;
}

/** The page itself. A step of the job flow links to its screen only when
 * this account can open that screen -- a crew reading Help is not sent to
 * an office screen that would refuse them. */
export function HelpView({
  reachable,
  help,
  helpError,
}: {
  reachable: ReadonlySet<string>;
  help: Help | undefined;
  helpError: string | null;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set([QUESTIONS[0].id]));
  const searching = query.trim() !== "";
  const shown = QUESTIONS.filter((q) => matchesQuestion(q, query));

  function toggle(id: string, isOpen: boolean) {
    setOpen((current) => {
      const next = new Set(current);
      if (isOpen) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  return (
    <div className="help-page">
      <div className="help-top">
        <h1>Help</h1>
        <input
          type="search"
          className="help-search"
          placeholder="Search help, e.g. “assign sites” or “price a line”"
          aria-label="Search help"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <section className="help-section" aria-labelledby="help-flow">
        <h2 id="help-flow" className="section-label">
          How a job flows
        </h2>
        <ol className="help-flow">
          {JOB_FLOW.map((step, index) => {
            const body = (
              <>
                <span className="help-step-num">{index + 1}</span>
                <strong>{step.title}</strong>
                <span className="help-step-body">{step.body}</span>
              </>
            );
            return (
              <li key={step.title}>
                {reachable.has(step.href) ? (
                  <Link className="help-step" to={step.href}>
                    {body}
                  </Link>
                ) : (
                  <div className="help-step">{body}</div>
                )}
              </li>
            );
          })}
        </ol>
      </section>

      <section className="help-section" aria-labelledby="help-questions">
        <h2 id="help-questions" className="section-label">
          Common questions
        </h2>
        {shown.map((q) => (
          <details
            key={q.id}
            className="help-question"
            open={searching || open.has(q.id)}
            onToggle={(e) => {
              if (!searching) {
                toggle(q.id, e.currentTarget.open);
              }
            }}
          >
            <summary>
              <span>{q.question}</span>
              <span className="help-sign" aria-hidden="true" />
            </summary>
            <p>{q.answer}</p>
          </details>
        ))}
        {shown.length === 0 ? (
          <p className="help-none">Nothing matches “{query.trim()}”. Try fewer words, or ask the office administrator.</p>
        ) : null}
      </section>

      <section className="help-section" aria-labelledby="help-more">
        <h2 id="help-more" className="section-label">
          More help
        </h2>
        {helpError ? <p className="notice bad">The handbook links could not be loaded: {helpError}</p> : null}
        {help ? (
          <div className="help-more">
            {help.field_handbook_available ? (
              <a className="btn btn-primary" href={help.field_handbook_url}>
                Field Operator Handbook (Word)
              </a>
            ) : null}
            {help.handbook_available ? (
              <a className="btn" href={help.handbook_url}>
                PRV Maintenance Handbook (Word)
              </a>
            ) : (
              <span className="help-none">The handbook has not been built yet.</span>
            )}
            <a className="btn" href={help.walkthrough_url} target="_blank" rel="noreferrer">
              Watch the walkthrough (Google Drive)
            </a>
          </div>
        ) : null}
      </section>
    </div>
  );
}
